import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  RefreshCw,
  Save,
  Info,
  Eye,
  EyeOff,
  Activity,
  Baby,
  Heart,
  Users,
  UserMinus,
  CheckSquare,
  ClipboardCheck,
  Calendar,
  CalendarCheck,
  CalendarDays,
  Milestone,
  Star,
  PartyPopper,
  FileText,
  Loader2,
  Sun,
  Sunrise,
  Coffee,
  Moon,
  MessageSquare,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

interface ActivityTypeConfig {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const ACTIVITY_TYPES: ActivityTypeConfig[] = [
  {
    key: 'pregnancy_created',
    label: 'Schwangerschaft gestartet',
    description: 'Wenn eine neue Schwangerschaft angelegt wird',
    icon: <Baby className="w-4 h-4 text-pink-500" />,
  },
  {
    key: 'pregnancy_birth',
    label: 'Geburt',
    description: 'Wenn eine Schwangerschaft mit Geburt abgeschlossen wird',
    icon: <PartyPopper className="w-4 h-4 text-amber-500" />,
  },
  {
    key: 'pregnancy_ended',
    label: 'Schwangerschaft beendet',
    description: 'Wenn eine Schwangerschaft neutral beendet wird',
    icon: <FileText className="w-4 h-4 text-slate-500" />,
  },
  {
    key: 'ssw_milestone',
    label: 'SSW-Meilensteine',
    description: 'Wichtige Wochen (SSW 12, 20, 27, 37, 40)',
    icon: <Milestone className="w-4 h-4 text-purple-500" />,
  },
  {
    key: 'ssw_change',
    label: 'SSW-Wechsel',
    description: 'Jeder Wechsel in eine neue Schwangerschaftswoche',
    icon: <CalendarDays className="w-4 h-4 text-orange-500" />,
  },
  {
    key: 'partner_connected',
    label: 'Partner verbunden',
    description: 'Wenn ein Partner erfolgreich verbunden wird',
    icon: <Users className="w-4 h-4 text-blue-500" />,
  },
  {
    key: 'partner_disconnected',
    label: 'Partner getrennt',
    description: 'Wenn eine Partner-Verbindung aufgelöst wird',
    icon: <UserMinus className="w-4 h-4 text-slate-400" />,
  },
  {
    key: 'checklist_item_completed',
    label: 'Checklisten-Element abgehakt',
    description: 'Wenn ein Checklisten-Punkt erledigt wird',
    icon: <CheckSquare className="w-4 h-4 text-emerald-500" />,
  },
  {
    key: 'checklist_completed',
    label: 'Checkliste vollständig',
    description: 'Wenn eine komplette Checkliste abgeschlossen wird',
    icon: <ClipboardCheck className="w-4 h-4 text-teal-500" />,
  },
  {
    key: 'name_match',
    label: 'Namen-Match',
    description: 'Wenn beide Partner denselben Namen liken',
    icon: <Heart className="w-4 h-4 text-rose-500" />,
  },
  {
    key: 'name_favorited',
    label: 'Name favorisiert',
    description: 'Wenn ein Name als Favorit markiert wird',
    icon: <Star className="w-4 h-4 text-yellow-500" />,
  },
  {
    key: 'appointment_created',
    label: 'Termin erstellt',
    description: 'Wenn ein neuer Termin angelegt wird',
    icon: <Calendar className="w-4 h-4 text-cyan-500" />,
  },
  {
    key: 'appointment_completed',
    label: 'Termin wahrgenommen',
    description: 'Wenn ein Termin als erledigt markiert wird',
    icon: <CalendarCheck className="w-4 h-4 text-green-500" />,
  },
];

interface TimeSlotConfig {
  slot: string;
  label: string;
  timeRange: string;
  icon: React.ReactNode;
}

const TIME_SLOTS: TimeSlotConfig[] = [
  { slot: '00-06', label: 'Nacht', timeRange: '00:00 - 06:00', icon: <Moon className="w-4 h-4 text-indigo-400" /> },
  { slot: '06-11', label: 'Morgen', timeRange: '06:00 - 11:00', icon: <Sunrise className="w-4 h-4 text-orange-400" /> },
  { slot: '11-13', label: 'Mittag', timeRange: '11:00 - 13:00', icon: <Coffee className="w-4 h-4 text-amber-500" /> },
  { slot: '13-18', label: 'Nachmittag', timeRange: '13:00 - 18:00', icon: <Sun className="w-4 h-4 text-yellow-500" /> },
  { slot: '18-00', label: 'Abend', timeRange: '18:00 - 00:00', icon: <Moon className="w-4 h-4 text-purple-400" /> },
];

interface GreetingSettings {
  [slot: string]: string;
}

const DEFAULT_GREETINGS: Record<string, GreetingSettings> = {
  de: {
    '00-06': 'Gute Nacht, {name}',
    '06-11': 'Guten Morgen, {name}',
    '11-13': 'Mahlzeit, {name}',
    '13-18': 'Guten Tag, {name}',
    '18-00': 'Guten Abend, {name}',
  },
  en: {
    '00-06': 'Good night, {name}',
    '06-11': 'Good morning, {name}',
    '11-13': 'Enjoy your lunch, {name}',
    '13-18': 'Good afternoon, {name}',
    '18-00': 'Good evening, {name}',
  },
};

const LOCALE_TABS = [
  { code: 'de' as const, flag: '🇩🇪', label: 'DE' },
  { code: 'en' as const, flag: '🇬🇧', label: 'EN' },
];

export function DashboardSettingsPage() {
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [showWissenswertes, setShowWissenswertes] = useState(true);
  const [showActivityFeed, setShowActivityFeed] = useState(true);
  const [enabledTypes, setEnabledTypes] = useState<string[]>([]);
  const [dashboardCount, setDashboardCount] = useState(5);
  const [greetingLocale, setGreetingLocale] = useState<'de' | 'en'>('de');
  const [greetingsByLocale, setGreetingsByLocale] = useState<Record<string, GreetingSettings>>({
    de: { ...DEFAULT_GREETINGS.de },
    en: { ...DEFAULT_GREETINGS.en },
  });

  // Fetch current settings
  const { isLoading, refetch } = useQuery({
    queryKey: ['dashboard-settings'],
    queryFn: async () => {
      // Fetch system settings
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [
          'show_wissenswertes',
          'show_activity_feed',
          'activity_feed_enabled_types',
          'activity_feed_dashboard_count',
        ]);

      if (error) throw error;

      const settingsMap: Record<string, unknown> = {};
      (data as { key: string; value: unknown }[])?.forEach((s) => {
        settingsMap[s.key] = s.value;
      });

      // Initialize state from settings
      setShowWissenswertes(settingsMap['show_wissenswertes'] !== false);
      setShowActivityFeed(settingsMap['show_activity_feed'] !== false);

      const types = settingsMap['activity_feed_enabled_types'] as string[] | undefined;
      setEnabledTypes(types || ACTIVITY_TYPES.map((t) => t.key));

      const count = settingsMap['activity_feed_dashboard_count'] as number | undefined;
      setDashboardCount(count || 5);

      // Fetch greeting settings for all locales
      const { data: greetingsData, error: greetingsError } = await supabase
        .from('dashboard_greetings')
        .select('time_slot, greeting_text, locale');

      if (!greetingsError && greetingsData) {
        const byLocale: Record<string, GreetingSettings> = {
          de: { ...DEFAULT_GREETINGS.de },
          en: { ...DEFAULT_GREETINGS.en },
        };
        greetingsData.forEach((g: { time_slot: string; greeting_text: string; locale?: string }) => {
          const loc = g.locale || 'de';
          if (!byLocale[loc]) byLocale[loc] = {};
          byLocale[loc][g.time_slot] = g.greeting_text;
        });
        setGreetingsByLocale(byLocale);
      }

      return settingsMap;
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const updates = [
        { key: 'show_wissenswertes', value: showWissenswertes },
        { key: 'show_activity_feed', value: showActivityFeed },
        { key: 'activity_feed_enabled_types', value: enabledTypes },
        { key: 'activity_feed_dashboard_count', value: dashboardCount },
      ];

      for (const { key, value } of updates) {
        const { data: existing } = await supabase
          .from('system_settings')
          .select('key')
          .eq('key', key)
          .single();

        if (existing) {
          const { error } = await supabase
            .from('system_settings')
            .update({ value, updated_at: new Date().toISOString() } as never)
            .eq('key', key);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('system_settings')
            .insert({ key, value } as never);
          if (error) throw error;
        }
      }

      // Save greeting settings for all locales
      for (const [locale, greetingsForLocale] of Object.entries(greetingsByLocale)) {
        for (const [slot, text] of Object.entries(greetingsForLocale)) {
          const { error } = await supabase
            .from('dashboard_greetings')
            .upsert(
              { time_slot: slot, greeting_text: text, locale, updated_at: new Date().toISOString() } as never,
              { onConflict: 'time_slot,locale' }
            );
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-settings'] });
      setHasChanges(false);
      toast.success('Einstellungen gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  const handleToggleType = (typeKey: string) => {
    setEnabledTypes((prev) => {
      if (prev.includes(typeKey)) {
        return prev.filter((t) => t !== typeKey);
      }
      return [...prev, typeKey];
    });
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    setEnabledTypes(ACTIVITY_TYPES.map((t) => t.key));
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    setEnabledTypes([]);
    setHasChanges(true);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard-Einstellungen</h1>
            <p className="text-slate-600">Konfiguriere die Anzeige im User-Dashboard</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              title="Aktualisieren"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={!hasChanges || saveMutation.isPending}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Speichern
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Einstellungen...</div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Wissenswertes Section */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-teal-500" />
                Wissenswertes-Sektion
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">Wissenswertes anzeigen</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Zeigt die SSW-spezifischen Informationen und das Wissensarchiv im Dashboard an
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowWissenswertes(!showWissenswertes);
                    setHasChanges(true);
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    showWissenswertes ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform flex items-center justify-center ${
                      showWissenswertes ? 'translate-x-7' : ''
                    }`}
                  >
                    {showWissenswertes ? (
                      <Eye className="w-3.5 h-3.5 text-teal-500" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Activity Feed Section Toggle */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-500" />
                Activity-Feed-Sektion
              </h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">Activity Feed anzeigen</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Zeigt die letzten Aktivitäten im Dashboard an
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowActivityFeed(!showActivityFeed);
                    setHasChanges(true);
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    showActivityFeed ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform flex items-center justify-center ${
                      showActivityFeed ? 'translate-x-7' : ''
                    }`}
                  >
                    {showActivityFeed ? (
                      <Eye className="w-3.5 h-3.5 text-teal-500" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Greeting Configuration */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-teal-500" />
                  Tageszeit-Begrüßungen
                </h2>
                <div className="flex items-center gap-1 bg-slate-200 rounded-lg p-1">
                  {LOCALE_TABS.map(({ code, flag, label }) => (
                    <button
                      key={code}
                      onClick={() => setGreetingLocale(code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        greetingLocale === code
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      <span>{flag}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-500 mb-4">
                Konfiguriere die Begrüßungstexte für verschiedene Tageszeiten. Nutze <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700">{'{name}'}</code> als Platzhalter für den Namen.
              </p>
              <div className="space-y-4">
                {TIME_SLOTS.map((slot) => (
                  <div key={slot.slot} className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-slate-50/50">
                    <div className="flex items-center gap-2 min-w-[140px]">
                      <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        {slot.icon}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 text-sm">{slot.label}</div>
                        <div className="text-xs text-slate-500">{slot.timeRange}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={greetingsByLocale[greetingLocale]?.[slot.slot] || ''}
                        onChange={(e) => {
                          setGreetingsByLocale((prev) => ({
                            ...prev,
                            [greetingLocale]: {
                              ...prev[greetingLocale],
                              [slot.slot]: e.target.value,
                            },
                          }));
                          setHasChanges(true);
                        }}
                        placeholder={`z.B. ${greetingLocale === 'de' ? `Guten ${slot.label}` : 'Good morning'}, {name}`}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Vorschau ({greetingLocale.toUpperCase()}):</strong> Der aktuelle Gruß würde lauten: &quot;{(() => {
                    const hour = new Date().getHours();
                    let slot = '18-00';
                    if (hour >= 0 && hour < 6) slot = '00-06';
                    else if (hour >= 6 && hour < 11) slot = '06-11';
                    else if (hour >= 11 && hour < 13) slot = '11-13';
                    else if (hour >= 13 && hour < 18) slot = '13-18';
                    return (greetingsByLocale[greetingLocale]?.[slot] || '').replace('{name}', 'Lisa');
                  })()}&quot;
                </p>
              </div>
            </div>
          </div>

          {/* Activity Feed Configuration */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                Activity Feed Konfiguration
              </h2>
            </div>

            {/* Dashboard Count */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">Anzahl im Dashboard-Widget</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Wie viele Aktivitäten im Dashboard-Widget angezeigt werden (5-20)
                  </p>
                </div>
                <input
                  type="number"
                  min={5}
                  max={20}
                  value={dashboardCount}
                  onChange={(e) => {
                    const val = Math.min(20, Math.max(5, parseInt(e.target.value) || 5));
                    setDashboardCount(val);
                    setHasChanges(true);
                  }}
                  className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-center focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>
            </div>

            {/* Activity Types */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-medium text-slate-900">Aktivitätstypen</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Welche Aktivitäten im Feed angezeigt werden
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                  >
                    Alle auswählen
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-sm text-slate-500 hover:text-slate-700"
                  >
                    Alle abwählen
                  </button>
                </div>
              </div>

              <div className="grid gap-3">
                {ACTIVITY_TYPES.map((type) => {
                  const isEnabled = enabledTypes.includes(type.key);
                  return (
                    <div
                      key={type.key}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-colors cursor-pointer ${
                        isEnabled
                          ? 'border-teal-200 bg-teal-50/50'
                          : 'border-slate-200 bg-slate-50/50'
                      }`}
                      onClick={() => handleToggleType(type.key)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isEnabled ? 'bg-teal-100' : 'bg-slate-200'
                          }`}
                        >
                          {type.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 text-sm">{type.label}</h4>
                          <p className="text-xs text-slate-500">{type.description}</p>
                        </div>
                      </div>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isEnabled
                            ? 'bg-teal-500 border-teal-500'
                            : 'bg-white border-slate-300'
                        }`}
                      >
                        {isEnabled && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800">Hinweis</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Änderungen wirken sich sofort auf alle Nutzer aus. Deaktivierte Aktivitätstypen
                  werden im Feed ausgeblendet, aber nicht gelöscht.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
